export type TutorialPageKey = 'home' | 'margin' | 'ledger' | 'documents' | 'dashboard' | 'capital' | 'perks';

export type TutorialStep = {
    target?: string;
    anchorTarget?: string;
    targetAll?: boolean;
    enterEvent?: string;
    tooltipOffsetX?: number;
    sideOffsetX?: number;
    sideOffsetY?: number;
    arrowOffsetX?: number;
    arrowOffset?: number;
    positionFromHighlight?: boolean;
    alignArrowToAnchor?: boolean;
    title: string;
    body: string;
    padding?: number;
    highlight?: boolean;
    dim?: boolean;
    placement?: 'right' | 'left' | 'bottom' | 'top';
};

export type TutorialConfig = {
    pageKey: TutorialPageKey;
    version: number;
    steps: TutorialStep[];
};

export const tutorialConfigs: Record<TutorialPageKey, TutorialConfig> = {
    home: {
        pageKey: 'home',
        version: 1,
        steps: [
            {
                target: '[data-tutorial-nav="home"]',
                title: 'Your weekly action list, ranked by impact',
                body: 'Signals surface the risks and opportunities that matter most, so you know what to act on first.',
                padding: 6,
                highlight: false,
                placement: 'right',
            },
            {
                target: '[data-tutorial="home-snapshot"]',
                anchorTarget: '[data-tutorial="home-snapshot-primary"]',
                title: 'See the full picture at a glance',
                body: 'Quickly assess how you closed, where you are heading, and what could impact outcomes.',
                padding: 0,
                placement: 'bottom',
            },
            {
                target: '[data-tutorial="signal-first-row"]',
                anchorTarget: '[data-tutorial="signal-status-button"]',
                title: 'Act on what matters most',
                body: 'Each signal shows the issue, dollar impact, and the action needed before it affects cash flow further.',
                padding: 0,
            },
        ],
    },
    margin: {
        pageKey: 'margin',
        version: 1,
        steps: [
            {
                target: '[data-tutorial-nav="margin"]',
                title: 'Control margin before books close',
                body: 'Finz reconciles bank, accounting, vendor, AR, and AP data into a live margin view.',
                padding: 6,
                highlight: false,
                placement: 'right',
            },
            {
                target: '[data-tutorial="margin-metrics"]',
                anchorTarget: '[data-tutorial="margin-metric-wtd-primary"]',
                targetAll: true,
                title: 'See margin in real time',
                body: 'Track revenue, gross profit, OpEx, and operating profit across the week and month.',
                padding: 0,
                placement: 'bottom',
                tooltipOffsetX: 64,
                arrowOffset: 20,
            },
            {
                target: '[data-tutorial="margin-uncategorized-column"]',
                targetAll: true,
                title: 'Catch what needs review',
                body: 'Finz tags and organizes transactions automatically, then flags the items that still need attention.',
                padding: 0,
                placement: 'bottom',
                positionFromHighlight: true,
            },
            {
                target: '[data-tutorial="margin-drilldown-first-row"]',
                anchorTarget: '[data-tutorial="margin-drilldown-status"]',
                enterEvent: 'finz:tutorial:margin-open-uncategorized',
                title: 'Resolve it with AI or manual review',
                body: 'Use the AI agent or update the category yourself to improve the P&L without waiting on close.',
                padding: 0,
                placement: 'bottom',
            },
        ],
    },
    ledger: {
        pageKey: 'ledger',
        version: 2,
        steps: [
            {
                target: '[data-tutorial-nav="ledger"]',
                title: 'The data layer behind Finz',
                body: 'See the structured transactions behind cash, costs, and working capital.',
                padding: 6,
                highlight: false,
                placement: 'right',
            },
            {
                target: '[data-tutorial="ledger-view-select"]',
                enterEvent: 'finz:tutorial:ledger-open-view-dropdown',
                title: 'Switch views across the business',
                body: 'Move between cash, customers, vendors, inventory, banking, and accounting in one place.',
                padding: 2,
                dim: false,
                placement: 'left',
                sideOffsetY: -60,
            },
            {
                target: '[data-tutorial="ledger-table"]',
                enterEvent: 'finz:tutorial:ledger-show-ar-customers',
                title: 'Stay ahead of receivables',
                body: 'Track what is due in, review overdue balances, and surface collection priorities for the week.',
                padding: 8,
                highlight: false,
                dim: false,
                placement: 'top',
                tooltipOffsetX: -360,
                arrowOffsetX: -360,
            },
            {
                target: '[data-tutorial="ledger-ar-weekly-summary-modal"]',
                enterEvent: 'finz:tutorial:ledger-open-ar-summary',
                title: 'Stay ahead of receivables',
                body: 'Review collected, expected, missed, and overdue cash so collection actions stay tied to the customer ledger.',
                padding: 0,
                highlight: false,
                dim: false,
                placement: 'right',
                sideOffsetY: -110,
            },
        ],
    },
    documents: {
        pageKey: 'documents',
        version: 1,
        steps: [
            {
                target: '[data-tutorial-nav="documents"]',
                title: 'One Smart Drive for invoices and spreadsheets',
                body: 'Store the business inputs you already run on, then turn them into structured data inside Finz.',
                padding: 6,
                highlight: false,
                placement: 'right',
            },
            {
                target: '[data-tutorial="documents-type-value"]',
                title: 'Keep everything organized and trackable',
                body: 'Invoices, bills, expenses, and spreadsheets stay in one place, ready for review, sync, and workflow.',
                padding: 0,
                highlight: false,
                dim: false,
                placement: 'right',
                alignArrowToAnchor: true,
                sideOffsetX: -48,
            },
            {
                target: '[data-tutorial="documents-tutorial-upload-file"]',
                enterEvent: 'finz:tutorial:documents-open-upload',
                title: 'Turn invoices into AP workflow',
                body: 'Upload a bill, tag it once, and let Finz turn it into structured, trackable AP data.',
                padding: 0,
                placement: 'right',
            },
            {
                target: '[data-tutorial="documents-tutorial-type-select"]',
                enterEvent: 'finz:tutorial:documents-open-type-dropdown',
                title: 'Choose how Finz should process it',
                body: 'Document type controls whether Finz extracts invoice lines, expenses, statements, or reference files.',
                padding: 0,
                placement: 'right',
            },
            {
                target: '[data-tutorial="documents-tutorial-preview"]',
                enterEvent: 'finz:tutorial:documents-open-review',
                title: 'Review, sync, and track every bill',
                body: 'Confirm the parsed invoice, push it into QuickBooks, and track item-level pricing as new bills come in.',
                padding: 0,
                highlight: false,
                dim: false,
                placement: 'right',
                sideOffsetX: -560,
                sideOffsetY: -320,
            },
        ],
    },
    dashboard: {
        pageKey: 'dashboard',
        version: 1,
        steps: [
            {
                target: '[data-tutorial-nav="dashboard"]',
                title: 'Stay ahead of cash flow',
                body: 'See where the business stands now, what changed, what needs attention next.',
                padding: 6,
                highlight: false,
                placement: 'right',
            },
            {
                target: '[data-tutorial="dashboard-kpis"]',
                anchorTarget: '[data-tutorial="dashboard-kpi-primary"]',
                title: 'Get the live position',
                body: 'Current balance, cash in, cash out, and net cash, all in one view.',
                padding: 0,
                placement: 'bottom',
            },
            {
                target: '[data-tutorial="dashboard-cash-flow-trend"]',
                title: 'See what changed',
                body: 'Review recent movement across inflows and outflows over time.',
                padding: 0,
                placement: 'right',
            },
            {
                target: '[data-tutorial="dashboard-cash-outlook"]',
                title: 'See what is coming next',
                body: 'Track projected cash flow and upcoming obligations before they hit.',
                padding: 0,
                placement: 'left',
            },
        ],
    },
    capital: {
        pageKey: 'capital',
        version: 1,
        steps: [
            {
                target: '[data-tutorial-nav="capital"]',
                title: 'Understand capital readiness',
                body: 'Review the signals and metrics lenders care about before you apply for financing.',
                padding: 6,
                highlight: false,
                placement: 'right',
            },
        ],
    },
    perks: {
        pageKey: 'perks',
        version: 1,
        steps: [
            {
                target: '[data-tutorial-nav="perks"]',
                title: 'Find relevant perks',
                body: 'Browse offers and tools that can help lower costs or improve operations.',
                padding: 6,
                highlight: false,
                placement: 'right',
            },
        ],
    },
};

export const tutorialPageForPath = (pathname: string): TutorialPageKey | null => {
    if (pathname.startsWith('/settings') || pathname.startsWith('/history') || pathname.startsWith('/admin')) return null;
    if (pathname.startsWith('/home')) return 'home';
    if (pathname.startsWith('/margin')) return 'margin';
    if (pathname.startsWith('/ledger')) return 'ledger';
    if (pathname.startsWith('/drive') || pathname.startsWith('/documents')) return 'documents';
    if (pathname.startsWith('/cash') || pathname.startsWith('/dashboard')) return 'dashboard';
    if (pathname.startsWith('/capital')) return 'capital';
    if (pathname.startsWith('/perks')) return 'perks';
    return null;
};
